import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, Users, Bed, MapPin, GraduationCap, AlertCircle, CheckCircle } from 'lucide-react';

const RoomModal = ({ room, onClose, onApplicationSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [roomDetails, setRoomDetails] = useState(null);

  useEffect(() => {
    checkUserStatus();
    fetchRoomDetails();
  }, [room.id]);

  const checkUserStatus = async () => {
    try {
      const [roomResponse, applicationsResponse] = await Promise.all([
        axios.get('/api/applications/my-room'),
        axios.get('/api/applications/my-applications')
      ]);
      
      setUserStatus({
        currentRoom: roomResponse.data,
        applications: applicationsResponse.data
      });
    } catch (error) {
      console.error('Kullanıcı durumu kontrol edilemedi:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const fetchRoomDetails = async () => {
    try {
      // Cache'i temizlemek için timestamp ekle
      const timestamp = new Date().getTime();
      const random = Math.random();
      const response = await axios.get(`/api/rooms/${room.id}?t=${timestamp}&r=${random}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      setRoomDetails(response.data);
    } catch (error) {
      console.error('Oda detayları alınamadı:', error);
    }
  };

  const handleApplication = async () => {
    setLoading(true);
    try {
      await axios.post('/api/applications', { roomId: room.id });
      toast.success('Başvurunuz başarıyla gönderildi!');
      onApplicationSubmit();
      onClose();
    } catch (error) {
      const message = error.response?.data?.error || 'Başvuru gönderilemedi';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = () => {
    switch (room.status_color) {
      case 'green':
        return 'Boş';
      case 'orange':
        return 'Son 1 Kişi';
      case 'gray':
        return 'Dolu';
      case 'yellow':
        return 'Kısmen Dolu';
      default:
        return 'Bilinmiyor';
    }
  };

  const getStatusColor = () => {
    switch (room.status_color) {
      case 'green':
        return 'bg-green-100 text-green-800';
      case 'orange':
        return 'bg-orange-100 text-orange-800';
      case 'gray':
        return 'bg-gray-100 text-gray-800';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const canApply = () => {
    if (!userStatus) return false;
    
    // Zaten bir odada yaşıyorsa başvuru yapamaz
    if (userStatus.currentRoom) return false;
    
    // Aktif başvurusu varsa başvuru yapamaz
    const hasActiveApplication = userStatus.applications.some(app => app.status === 'pending');
    if (hasActiveApplication) return false;
    
    // Oda doluysa başvuru yapamaz
    if (room.available_beds === 0) return false;
    
    return true;
  };

  const getApplicationStatus = () => {
    if (!userStatus) return null;
    
    if (userStatus.currentRoom) {
      return {
        type: 'current-room',
        message: `Zaten ${userStatus.currentRoom.room_number} odasında yaşıyorsunuz. Oda değişikliği için yönetici ile iletişime geçin.`,
        icon: <CheckCircle className="w-5 h-5 text-green-600" />
      };
    }
    
    const activeApplication = userStatus.applications.find(app => app.status === 'pending');
    if (activeApplication) {
      return {
        type: 'pending-application',
        message: `Bekleyen bir başvurunuz var: Oda ${activeApplication.room_number}. Önce bu başvurunun sonucunu bekleyin.`,
        icon: <AlertCircle className="w-5 h-5 text-yellow-600" />
      };
    }
    
    const rejectedApplication = userStatus.applications.find(app => app.status === 'rejected');
    if (rejectedApplication) {
      return {
        type: 'rejected-application',
        message: `Önceki başvurunuz reddedildi. Yeni bir başvuru yapabilirsiniz.`,
        icon: <AlertCircle className="w-5 h-5 text-red-600" />
      };
    }
    
    return null;
  };

  const applicationStatus = getApplicationStatus();

  if (checkingStatus) {
    return (
      <div className="modal">
        <div className="modal-content">
          <div className="flex items-center justify-center h-32">
            <div className="text-lg text-gray-600">Kontrol ediliyor...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal">
      <div className="modal-content">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Oda {room.room_number}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Kullanıcı Durumu */}
          {applicationStatus && (
            <div className={`p-4 rounded-lg border ${
              applicationStatus.type === 'current-room' ? 'bg-green-50 border-green-200' :
              applicationStatus.type === 'pending-application' ? 'bg-yellow-50 border-yellow-200' :
              'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start space-x-3">
                {applicationStatus.icon}
                <div className="text-sm">
                  {applicationStatus.message}
                </div>
              </div>
            </div>
          )}

          {/* Oda Bilgileri */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-primary" />
              <div>
                <div className="text-sm text-gray-600">Oda Numarası</div>
                <div className="font-medium">{room.room_number}</div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Users className="w-5 h-5 text-green-600" />
              <div>
                <div className="text-sm text-gray-600">Kapasite</div>
                <div className="font-medium">{room.capacity} Kişi</div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Bed className="w-5 h-5 text-purple-600" />
              <div>
                <div className="text-sm text-gray-600">Boş Yatak</div>
                <div className="font-medium">{room.available_beds} Adet</div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-gray-600"></div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Durum</div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}>
                  {getStatusText()}
                </div>
              </div>
            </div>
          </div>

          {/* Oda Sakinleri */}
          {roomDetails && roomDetails.occupants && roomDetails.occupants.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <GraduationCap className="w-5 h-5 mr-2 text-primary" />
                Oda Sakinleri ({roomDetails.occupants.length}/{room.capacity})
              </h3>
              <div className="space-y-3">
                {roomDetails.occupants.map((occupant, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{occupant.displayText}</div>
                      <div className="text-sm text-gray-600">Öğrenci</div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {index + 1}. Kişi
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Boş Yataklar */}
          {roomDetails && room.available_beds > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Bed className="w-5 h-5 mr-2 text-green-600" />
                Boş Yataklar ({room.available_beds} adet)
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {Array.from({ length: room.available_beds }, (_, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg border border-green-200"
                  >
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Bed className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="text-sm text-gray-700">
                      Boş Yatak {(roomDetails.occupants ? roomDetails.occupants.length : 0) + index + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Başvuru Butonu */}
          {canApply() && (
            <div className="pt-4 border-t">
              <button
                onClick={handleApplication}
                disabled={loading}
                className="btn btn-primary w-full"
              >
                {loading ? 'Başvuru Gönderiliyor...' : 'Bu Odaya Başvur'}
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Başvurunuz yönetici onayından sonra değerlendirilecektir.
              </p>
            </div>
          )}

          {!canApply() && room.available_beds === 0 && (
            <div className="pt-4 border-t">
              <div className="text-center">
                <div className="text-lg font-medium text-gray-900 mb-2">
                  Bu oda dolu
                </div>
                <p className="text-sm text-gray-600">
                  Başka bir oda seçmeyi deneyebilirsiniz.
                </p>
              </div>
            </div>
          )}

          {!canApply() && room.available_beds > 0 && applicationStatus && (
            <div className="pt-4 border-t">
              <div className="text-center">
                <div className="text-lg font-medium text-gray-900 mb-2">
                  Başvuru yapamazsınız
                </div>
                <p className="text-sm text-gray-600">
                  Yukarıdaki durumu kontrol edin.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoomModal; 